import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCreateStripeVerificationSession } from "@src/appV2/redesign/Documents/Details/Workflows/Stripe/useCreateStripeVerificationSession";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { useCreateDocument } from "../../../api/useCreateDocument";

interface UploadDocumentAndGetVerificationSessionParams {
  personalIdSubtype: string;
  requirementId: string;
  autoUpdateWorkerName?: boolean;
}

/*
  These are placeholder values that we use to create a personal ID document to our backend
  while the user would upload the document through stripe identity
  Read more here: https://clipboardhealth.atlassian.net/wiki/spaces/EPP/pages/2551054487/Product+Brief+-+automating+ID+verification
  Link to a discussion on this: https://github.com/ClipboardHealth/cbh-mobile-app/pull/2452#discussion_r1333276671
*/
const PERSONAL_ID_PLACEHOLDER_FILE_NAME = "personal_id_placeholder";
const PERSONAL_ID_PLACEHOLDER_ROOT_KEY = "document-storage-service-cbh-dev";
const PERSONAL_ID_PLACEHOLDER_FILE_KEY =
  "635a84f62ebfd88fddceb269/5baabbd23b089e0004880e55/a2de0370-3dc1-4a54-9046-cf2c8ef59e76.jpeg";

export function useUploadDocumentAndGetVerificationSession() {
  const worker = useDefinedWorker();

  const {
    mutateAsync: createStripeVerificationSession,
    isLoading: isLoadingStripeVerificationSession,
  } = useCreateStripeVerificationSession();
  const { mutateAsync: createDocument, isLoading: isLoadingCreateDocument } = useCreateDocument();

  async function uploadDocumentAndGetVerificationSession(
    params: UploadDocumentAndGetVerificationSessionParams
  ) {
    const { personalIdSubtype, requirementId, autoUpdateWorkerName } = params;

    try {
      const document = await createDocument({
        data: {
          attributes: {
            name: PERSONAL_ID_PLACEHOLDER_FILE_NAME,
            requirementId,
            workerUserId: worker.userId,
            fileStorageRootFolder: PERSONAL_ID_PLACEHOLDER_ROOT_KEY,
            fileStorageFileKey: PERSONAL_ID_PLACEHOLDER_FILE_KEY,
            subType: personalIdSubtype,
          },
        },
      });

      const documentId = document.data.id;

      const response = await createStripeVerificationSession({
        hcpId: worker.userId,
        documentId,
        autoUpdateWorkerName,
      });

      logEvent(APP_V2_USER_EVENTS.UPLOAD_DOCUMENT_SUCCESS);

      const { clientSecret, verificationSessionId, ephemeralKeySecret } = response;

      return {
        documentId,
        clientSecret,
        verificationSessionId,
        ephemeralKeySecret,
      };
    } catch (error) {
      logEvent(APP_V2_USER_EVENTS.UPLOAD_DOCUMENT_FAILURE);
      throw error;
    }
  }

  return {
    uploadDocumentAndGetVerificationSession,
    isLoading: isLoadingStripeVerificationSession || isLoadingCreateDocument,
  };
}
